import { InventoryItemContainerProps } from "../InventoryItemSlot/InventoryItemContainer";
import { CharacterData } from "../../../../utils/game/character/CharacterData";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import InventoryItem from "../../../../utils/game/mechanics/InventoryItem";
import InventoryItemIconSlot from "../InventoryItemSlot/InventoryItemIconSlot";
import { KeyValuePair } from "../../../../utils/sys/KeyValuePair";
import { useCallback, useContext, useMemo, useState } from "react";
import InventoryItemTransferContext from "../../../../utils/sys/InventoryItemTransferContext";
import { UIInventoryItemContainerClickParams } from "../InventoryItemSlot/InventoryItemContainer";
import InventoryItemEditorModal from "../../../../common/components/modals/InventoryItemEditorModal/InventoryItemEditorModal";
import { Dictionary } from "../../../../utils/sys/Dictionary";

import "./CharacterEquipmentPanel_style.css";

type SlotProperties = Readonly<{
    id: string;
    restrictor: (item: InventoryItem | null) => boolean;
    setItemCallback: (item: InventoryItem | null) => void;
}>;

type Props = {
    characterData: CharacterData;
    isEditable?: boolean;
    onItemChanged?: (item: InventoryItem | null, slot: string) => void;
    onTransferReject?: () => void;
    onTransferSequenceComplete?: () => void;
};

export default function CharacterEquipmentPanel({
    characterData,
    isEditable,
    onItemChanged,
    onTransferReject,
    onTransferSequenceComplete,
}: Props): JSX.Element {
    const [isItemEditorVisible, setItemEditorVisibility] =
        useState<boolean>(false);
    const [itemEditorKey, setItemEditorKey] = useState<string | null>(null);
    const [itemEditorInitialData, setItemEditorInitialData] = useState<
        InventoryItem | undefined
    >(undefined);
    const { transferTargetItemContainer, setTransferTargetItemContainer } =
        useContext(InventoryItemTransferContext);

    const storedItemEquipTypes = useMemo(() => {
        return transferTargetItemContainer?.item?.equipTypes ?? [];
    }, [transferTargetItemContainer]);

    const itemSlotProperties: Dictionary<string, SlotProperties> = useMemo(
        () =>
            new Dictionary<string, SlotProperties>([
                {
                    key: "ammo",
                    value: {
                        id: `character_equipment_${characterData.id}-ammo`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("ammunition") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ammo");
                        },
                    },
                },
                {
                    key: "feet",
                    value: {
                        id: `character_equipment_${characterData.id}-feet`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("feet") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "feet");
                        },
                    },
                },
                {
                    key: "hands",
                    value: {
                        id: `character_equipment_${characterData.id}-hands`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("hands") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "hands");
                        },
                    },
                },
                {
                    key: "head",
                    value: {
                        id: `character_equipment_${characterData.id}-head`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("head") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "head");
                        },
                    },
                },
                {
                    key: "legs",
                    value: {
                        id: `character_equipment_${characterData.id}-legs`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("legs") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "legs");
                        },
                    },
                },
                {
                    key: "lh",
                    value: {
                        id: `character_equipment_${characterData.id}-lh`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("held") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "lh");
                        },
                    },
                },
                {
                    key: "neck",
                    value: {
                        id: `character_equipment_${characterData.id}-neck`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("neck") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "neck");
                        },
                    },
                },
                {
                    key: "rh",
                    value: {
                        id: `character_equipment_${characterData.id}-rh`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("held") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "rh");
                        },
                    },
                },
                {
                    key: "ring1",
                    value: {
                        id: `character_equipment_${characterData.id}-ring1`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring1");
                        },
                    },
                },
                {
                    key: "ring2",
                    value: {
                        id: `character_equipment_${characterData.id}-ring2`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring2");
                        },
                    },
                },
                {
                    key: "ring3",
                    value: {
                        id: `character_equipment_${characterData.id}-ring3`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring3");
                        },
                    },
                },
                {
                    key: "ring4",
                    value: {
                        id: `character_equipment_${characterData.id}-ring4`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring4");
                        },
                    },
                },
                {
                    key: "ring5",
                    value: {
                        id: `character_equipment_${characterData.id}-ring5`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring5");
                        },
                    },
                },
                {
                    key: "ring6",
                    value: {
                        id: `character_equipment_${characterData.id}-ring6`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("finger") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "ring6");
                        },
                    },
                },
                {
                    key: "torso",
                    value: {
                        id: `character_equipment_${characterData.id}-torso`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("torso") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "torso");
                        },
                    },
                },
                {
                    key: "waist",
                    value: {
                        id: `character_equipment_${characterData.id}-waist`,
                        restrictor: (item: InventoryItem | null) =>
                            item?.equipTypes.includes("waist") ?? true,
                        setItemCallback: (item: InventoryItem | null) => {
                            onItemChanged?.(item, "waist");
                        },
                    },
                },
            ]),
        [characterData.id, onItemChanged]
    );

    const startEditingItemSlot = useCallback(
        (key: string, item: InventoryItem | null) => {
            setItemEditorInitialData(item ?? undefined);
            setItemEditorKey(key);
            setItemEditorVisibility(true);
        },
        []
    );

    return (
        <div className="character-equipment-container">
            <div className="character-equipment-border">
                <FlexColumn gap={4}>
                    <FlexRow alignment="center" gap={4}>
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("ammo")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="arrow_blank"
                            item={characterData.ammo}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("ammo")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("ammo")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("ammo")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "ammunition"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "ammo",
                                    characterData.ammo
                                );
                            }}
                        />
                        <div className="item-slot-spacer" />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("head")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="helmet_blank"
                            item={characterData.head}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("head")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("head")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("head")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "head"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "head",
                                    characterData.head
                                );
                            }}
                        />
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("neck")?.id ?? ""}
                                className="item-slot"
                                emptyIconSrc="amulet_blank"
                                item={characterData.neck}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("neck")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("neck")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("neck")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "neck"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "neck",
                                        characterData.neck
                                    );
                                }}
                            />
                        </div>
                        <div className="item-slot-spacer" />
                    </FlexRow>
                    <FlexRow alignment="center" gap={4}>
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring1")?.id ?? ""}
                                className="item-slot"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring1}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring1")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring1")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring1")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring1",
                                        characterData.ring1
                                    );
                                }}
                            />
                        </div>
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("lh")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="lh_blank"
                            item={characterData.lh}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("lh")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("lh")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("lh")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "held"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot("lh", characterData.lh);
                            }}
                        />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("torso")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="shirt_blank"
                            item={characterData.torso}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("torso")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("torso")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("torso")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "torso"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "torso",
                                    characterData.torso
                                );
                            }}
                        />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("rh")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="rh_blank"
                            item={characterData.rh}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("rh")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("rh")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("rh")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "held"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot("rh", characterData.rh);
                            }}
                        />
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring4")?.id ?? ""}
                                className="item-slot"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring4}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring4")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring4")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring4")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring4",
                                        characterData.ring4
                                    );
                                }}
                            />
                        </div>
                    </FlexRow>
                    <FlexRow alignment="center" gap={4}>
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring2")?.id ?? ""}
                                className="item-slot left"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring2}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring2")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring2")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring2")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring2",
                                        characterData.ring2
                                    );
                                }}
                            />
                        </div>
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("hands")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="gloves_blank"
                            item={characterData.hands}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("hands")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("hands")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("hands")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "hands"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "hands",
                                    characterData.hands
                                );
                            }}
                        />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("legs")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="pants_blank"
                            item={characterData.legs}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("legs")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("legs")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("legs")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "legs"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "legs",
                                    characterData.legs
                                );
                            }}
                        />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("waist")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="belt_blank"
                            item={characterData.waist}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("waist")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("waist")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("waist")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "waist"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "waist",
                                    characterData.waist
                                );
                            }}
                        />
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring5")?.id ?? ""}
                                className="item-slot right"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring5}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring5")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring5")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring5")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring5",
                                        characterData.ring5
                                    );
                                }}
                            />
                        </div>
                    </FlexRow>
                    <FlexRow alignment="center" gap={4}>
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring3")?.id ?? ""}
                                className="item-slot right"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring3}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring3")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring3")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring3")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring3",
                                        characterData.ring3
                                    );
                                }}
                            />
                        </div>
                        <div className="item-slot-spacer" />
                        <InventoryItemIconSlot
                            id={itemSlotProperties.get("feet")?.id ?? ""}
                            className="item-slot"
                            emptyIconSrc="boots_blank"
                            item={characterData.feet}
                            transferVariables={{
                                setItemCallback:
                                    itemSlotProperties.get("feet")
                                        ?.setItemCallback,
                                onTransferSequenceComplete:
                                    onTransferSequenceComplete,
                            }}
                            isEditable={isEditable}
                            itemRestrictor={
                                itemSlotProperties.get("feet")?.restrictor
                            }
                            isSelected={
                                transferTargetItemContainer?.id != null &&
                                transferTargetItemContainer.id ===
                                    itemSlotProperties.get("feet")?.id
                            }
                            isValidTarget={storedItemEquipTypes.includes(
                                "feet"
                            )}
                            tooltipEnabled={true}
                            onLongClick={() => {
                                startEditingItemSlot(
                                    "feet",
                                    characterData.feet
                                );
                            }}
                        />
                        <div className="item-slot-spacer" />
                        <div className="item-slot-small">
                            <InventoryItemIconSlot
                                id={itemSlotProperties.get("ring6")?.id ?? ""}
                                className="item-slot left"
                                emptyIconSrc="ring_blank"
                                item={characterData.ring6}
                                transferVariables={{
                                    setItemCallback:
                                        itemSlotProperties.get("ring6")
                                            ?.setItemCallback,
                                    onTransferSequenceComplete:
                                        onTransferSequenceComplete,
                                }}
                                isEditable={isEditable}
                                itemRestrictor={
                                    itemSlotProperties.get("ring6")?.restrictor
                                }
                                isSelected={
                                    transferTargetItemContainer?.id != null &&
                                    transferTargetItemContainer.id ===
                                        itemSlotProperties.get("ring6")?.id
                                }
                                isValidTarget={storedItemEquipTypes.includes(
                                    "finger"
                                )}
                                tooltipEnabled={true}
                                onLongClick={() => {
                                    startEditingItemSlot(
                                        "ring6",
                                        characterData.ring6
                                    );
                                }}
                            />
                        </div>
                    </FlexRow>
                    {isItemEditorVisible && (
                        <InventoryItemEditorModal
                            isVisible={isItemEditorVisible}
                            onClose={() => {
                                setItemEditorVisibility(false);
                                setItemEditorInitialData(undefined);
                            }}
                            onConfirm={(itemData) => {
                                if (itemEditorKey != null) {
                                    onItemChanged?.(itemData, itemEditorKey);
                                    onTransferSequenceComplete?.();
                                }

                                setItemEditorKey(null);
                                setItemEditorVisibility(false);
                                setItemEditorInitialData(undefined);
                            }}
                            initialItemData={itemEditorInitialData}
                        />
                    )}
                </FlexColumn>
            </div>
        </div>
    );
}
